import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, ShieldCheck } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export default function SuperLogin() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { saveSession } = useAuth();
  const navigate = useNavigate();

  const forgotSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email: form.email, role: "superadmin", origin_url: window.location.origin });
      setForgotSent(true);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/auth/login", form);
      saveSession(data);
      toast.success("Welcome back, Super Admin");
      navigate("/super");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-900" data-testid="super-login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="super-auth-brand">
          <div className="w-9 h-9 rounded-xl border-2 border-zinc-900 bg-[#A7E4D3] flex items-center justify-center shadow-[3px_3px_0px_0px_#A7E4D3]">
            <Clock className="w-5 h-5" />
          </div>
          <span className="font-display font-black text-2xl text-white">MyTimetable.co.uk</span>
        </Link>
        <div className="nb-card p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-xl border-2 border-zinc-900 bg-[#1E3A8A] flex items-center justify-center shadow-[3px_3px_0px_0px_#18181B]">
              <ShieldCheck className="w-7 h-7 text-white" />
            </div>
            <h1 className="font-display font-black text-2xl mt-4" data-testid="super-login-heading">Platform admin</h1>
            <p className="text-sm text-zinc-500 mt-1">Super-admin access only.</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="nb-label" htmlFor="super-email">Email</label>
              <input id="super-email" type="email" className="nb-input mt-2" data-testid="super-email-input"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div>
              <label className="nb-label" htmlFor="super-password">Password</label>
              <input id="super-password" type="password" className="nb-input mt-2" data-testid="super-password-input"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            </div>
            <button type="submit" disabled={busy} data-testid="super-login-submit" className="nb-btn w-full bg-[#1E3A8A] text-white py-3">
              {busy ? "Checking…" : "Sign in"}
            </button>
          </form>
          {!showForgot && (
            <button type="button" onClick={() => setShowForgot(true)} data-testid="super-forgot-link"
              className="w-full text-center text-xs font-bold text-zinc-500 hover:text-zinc-900 mt-4 transition-colors duration-150">
              Forgot your password?
            </button>
          )}
          {showForgot && (
            <div className="mt-4 pt-4 border-t-2 border-zinc-100" data-testid="super-forgot-form">
              {forgotSent ? (
                <p className="text-sm font-bold text-zinc-600 text-center" data-testid="super-forgot-sent">If that email is the super-admin account, a reset link is on its way. It lasts 1 hour.</p>
              ) : (
                <form onSubmit={forgotSubmit} className="flex gap-2">
                  <input type="email" className="nb-input flex-1" data-testid="super-forgot-email" placeholder="Super admin email"
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                  <button type="submit" data-testid="super-forgot-submit" className="nb-btn bg-[#FDE68A] px-4 py-2 text-sm shrink-0">Email reset link</button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
